import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import { isCanceledError } from 'src/helper/api';
import {
  fetchCompanyEligibleVendors,
  fetchCompanyVendorPreferences,
  resetCompanyCategoryDefault,
  setCompanyCategoryDefault,
} from 'src/redux/vendor-preferences/api';
import type {
  CompanyCategoryNode,
  CompanyVendorPreferenceResponse,
  EligibleVendor,
  VendorPreferenceError,
  VendorPreferenceListResource,
  VendorPreferencesState,
} from 'src/redux/vendor-preferences/types';

// The "Company (all markets)" view's thunk lifecycle (6.6, Q14). Everything lands in
// `state.company`, never in the market tree or its caches.

type CompanyArg = { problem_category_uuid?: string };

const isCanceled = (action: { payload?: unknown; error?: unknown }): boolean =>
  isCanceledError(action.payload) || isCanceledError(action.error);

const errorFromPayload = (payload: unknown): VendorPreferenceError => {
  if (payload && typeof payload === 'object') {
    const { status, message } = payload as { status?: number; message?: string };
    return { status, message };
  }
  return { message: 'Request failed' };
};

const argOf = (action: { meta?: { arg?: unknown } }): CompanyArg =>
  (action.meta?.arg as CompanyArg | undefined) ?? {};

const recomputePendingCount = (state: VendorPreferencesState) => {
  const { company } = state;
  company.pendingCount = company.categoryOrder.reduce(
    (count, uuid) => (company.categoriesById[uuid]?.status === 'pending' ? count + 1 : count),
    0,
  );
};

const companyExtraReducers = (builder: ActionReducerMapBuilder<VendorPreferencesState>) => {
  // ---- load the company's trade lists -------------------------------------
  builder.addCase(fetchCompanyVendorPreferences().pending, (state) => {
    state.company.isLoading = true;
    state.company.loadError = null;
  });

  builder.addCase(fetchCompanyVendorPreferences().fulfilled, (state, action) => {
    const payload = action.payload as CompanyVendorPreferenceResponse | undefined;
    const { company } = state;
    company.isLoading = false;
    company.hasLoaded = true;
    const categoriesById: Record<string, CompanyCategoryNode> = {};
    const categoryOrder: string[] = [];
    (payload?.categories ?? []).forEach((category) => {
      if (!category?.problem_category_uuid) return;
      categoriesById[category.problem_category_uuid] = {
        problem_category_uuid: category.problem_category_uuid,
        label: category.label,
        slug: category.slug,
        status: category.status,
        hasCompanyList: category.default_source === 'company',
        ranking: category.default_vendors ?? [],
      };
      categoryOrder.push(category.problem_category_uuid);
    });
    company.categoriesById = categoriesById;
    company.categoryOrder = categoryOrder;
    company.pendingCount = payload?.pending_count ?? 0;
  });

  builder.addCase(fetchCompanyVendorPreferences().rejected, (state, action) => {
    if (isCanceled(action)) return;
    state.company.isLoading = false;
    state.company.loadError = errorFromPayload(action.payload);
  });

  // ---- the company pool for one trade's "+ Add" picker ---------------------
  builder.addCase(fetchCompanyEligibleVendors().pending, (state, action) => {
    const uuid = argOf(action).problem_category_uuid;
    if (!uuid) return;
    delete state.company.eligibleErrorByCategory[uuid];
    state.company.eligibleLoadingByCategory[uuid] = true;
  });

  builder.addCase(fetchCompanyEligibleVendors().fulfilled, (state, action) => {
    const uuid = argOf(action).problem_category_uuid;
    if (!uuid) return;
    // The BE answers a bare array; tolerate a wrapped `{ data }` like the market pool does.
    const payload = action.payload as EligibleVendor[] | { data?: EligibleVendor[] } | undefined;
    state.company.eligibleByCategory[uuid] = Array.isArray(payload)
      ? payload
      : (payload?.data ?? []);
    delete state.company.eligibleLoadingByCategory[uuid];
  });

  builder.addCase(fetchCompanyEligibleVendors().rejected, (state, action) => {
    if (isCanceled(action)) return;
    const uuid = argOf(action).problem_category_uuid;
    if (!uuid) return;
    delete state.company.eligibleLoadingByCategory[uuid];
    state.company.eligibleErrorByCategory[uuid] = true;
  });

  // ---- writes ---------------------------------------------------------------
  const writes = [setCompanyCategoryDefault(), resetCompanyCategoryDefault()];
  writes.forEach((thunk) => {
    builder.addCase(thunk.pending, (state) => {
      state.company.isSaving = true;
      state.company.saveError = null;
    });
    builder.addCase(thunk.rejected, (state, action) => {
      if (isCanceled(action)) return;
      state.company.isSaving = false;
      state.company.saveError = errorFromPayload(action.payload);
    });
  });

  // Save replaces the company list's ranking; the trade now has a company list.
  builder.addCase(setCompanyCategoryDefault().fulfilled, (state, action) => {
    state.company.isSaving = false;
    const uuid = argOf(action).problem_category_uuid;
    const node = uuid ? state.company.categoriesById[uuid] : undefined;
    const resource = action.payload as VendorPreferenceListResource | undefined;
    if (!node || !resource) return;
    node.ranking = resource.vendors ?? [];
    node.status = node.ranking.length > 0 ? 'configured' : 'pending';
    node.hasCompanyList = true;
    recomputePendingCount(state);
  });

  // Reset deletes the company list (the BE answers with a message).
  builder.addCase(resetCompanyCategoryDefault().fulfilled, (state, action) => {
    state.company.isSaving = false;
    const uuid = argOf(action).problem_category_uuid;
    const node = uuid ? state.company.categoriesById[uuid] : undefined;
    if (!node) return;
    node.ranking = [];
    node.status = 'pending';
    node.hasCompanyList = false;
    recomputePendingCount(state);
  });
};

export default companyExtraReducers;
